import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import customerApi from "@/lib/customerApi";
import { toast } from "sonner";
import { User, Loader2, Save, CheckCircle2, Bell, BellOff, Share, Plus, Smartphone, X, MapPin, Star, Trash2 } from "lucide-react";
import {
  ensureCustomerPushSubscription,
  unsubscribeCustomerPush,
  isCustomerPushSubscribed,
  detectBrowser,
} from "@/lib/customerPush";

/**
 * v2.6.9 batch BE · Rider Profile tab
 * Saved personal info pre-fills booking forms. PATCH /api/customer/me
 * accepts: name, first_name, last_name, phone, address, city, state,
 * zip_code, photo_url.
 */
export default function RiderProfileTab({ customer, onSaved }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "",
    address: "", city: "", state: "", zip_code: "", email: "",
    date_of_birth: "",
    gender: "unspecified", preferred_driver_gender: "any",
    prefers_sms: true, prefers_email: true, prefers_push: false,
  });
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  // v2.7.65+E93 · Push subscription state
  const [pushBusy, setPushBusy] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushMeta] = useState(() => detectBrowser());
  // v2.7.65+E97 · iOS Safari A2HS install guide
  const [showIosA2hs, setShowIosA2hs] = useState(false);

  useEffect(() => {
    setPushSubscribed(isCustomerPushSubscribed());
  }, []);

  useEffect(() => {
    if (!customer) return;
    // Customer object may already carry these fields (we GET /customer/me on login)
    setForm((f) => ({
      ...f,
      first_name: customer.first_name || (customer.name || "").split(" ")[0] || "",
      last_name:  customer.last_name  || (customer.name || "").split(" ").slice(1).join(" ") || "",
      phone:      customer.phone || "",
      address:    customer.address || "",
      city:       customer.city || "",
      state:      customer.state || "",
      zip_code:   customer.zip_code || "",
      email:      customer.email || "",
      date_of_birth: customer.date_of_birth || "",
      gender:     customer.gender || "unspecified",
      preferred_driver_gender: customer.preferred_driver_gender || "any",
      prefers_sms:   customer.prefers_sms !== false, // default true
      prefers_email: customer.prefers_email !== false, // default true
      prefers_push:  customer.prefers_push === true, // default false — must be explicitly enabled
    }));
  }, [customer]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (v) => setForm((f) => ({ ...f, [k]: Boolean(v) }));

  // v2.7.65+E93 · Enable browser push (subscribe + save preference).
  const enablePush = async () => {
    if (pushBusy) return;
    // v2.7.65+E97 · iOS Safari-in-browser cannot subscribe until the
    // user adds Buller Taxi to their Home Screen. Show the guided
    // install modal instead of silent-fail.
    if (pushMeta.family === "safari" && !pushMeta.isPWA) {
      setShowIosA2hs(true);
      return;
    }
    setPushBusy(true);
    try {
      const res = await ensureCustomerPushSubscription();
      if (res.subscribed) {
        setPushSubscribed(true);
        setForm((f) => ({ ...f, prefers_push: true }));
        toast.success("Browser notifications enabled · you'll get instant driver-assigned pings");
      } else {
        const reason = res.reason || "unknown";
        if (reason === "permission_denied") {
          toast.error("Notifications are blocked · unblock the site in your browser settings, then try again");
        } else if (reason === "safari_not_pwa") {
          setShowIosA2hs(true);
        } else if (reason === "unsupported") {
          toast.error("This browser doesn't support notifications · we'll still text/email you");
        } else {
          toast.error(`Couldn't enable push · ${reason}`);
        }
      }
    } finally {
      setPushBusy(false);
    }
  };

  // v2.7.65+E93 · Disable push (unsubscribe from THIS device + save preference).
  const disablePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      await unsubscribeCustomerPush();
      setPushSubscribed(false);
      setForm((f) => ({ ...f, prefers_push: false }));
      try {
        await customerApi.patch("/customer/me", { prefers_push: false });
      } catch (_e) { /* silent */ }
      toast.success("Browser notifications disabled on this device");
    } finally {
      setPushBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        name: `${form.first_name} ${form.last_name}`.trim() || null,
        phone: form.phone || null,
        address: form.address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || null,
        preferred_driver_gender: form.preferred_driver_gender || null,
        prefers_sms: !!form.prefers_sms,
        prefers_email: !!form.prefers_email,
        prefers_push: !!form.prefers_push,
      };
      const { data } = await customerApi.patch("/customer/me", payload);
      setSavedAt(new Date().toISOString());
      toast.success("Profile saved");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
    <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="rider-profile-tab">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-amber-400/10 border border-amber-400/30 flex items-center justify-center text-amber-400 flex-shrink-0">
            <User className="h-5 w-5"/>
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate">My Profile</h2>
            <p className="text-xs text-zinc-400">Saved info auto-fills your booking forms</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">First Name</Label>
          <Input data-testid="rider-profile-first-name" value={form.first_name} onChange={set("first_name")} className="bg-zinc-950 border-zinc-700 h-11 mt-1" placeholder="First name"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Last Name</Label>
          <Input data-testid="rider-profile-last-name" value={form.last_name} onChange={set("last_name")} className="bg-zinc-950 border-zinc-700 h-11 mt-1" placeholder="Last name"/>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Email (read-only)</Label>
          <Input data-testid="rider-profile-email" value={form.email} readOnly className="bg-zinc-900 border-zinc-700 h-11 mt-1 text-zinc-500"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Phone</Label>
          <Input data-testid="rider-profile-phone" value={form.phone} onChange={set("phone")} className="bg-zinc-950 border-zinc-700 h-11 mt-1" placeholder="(580) 555-0100" inputMode="tel"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Zip Code</Label>
          <Input data-testid="rider-profile-zip" value={form.zip_code} onChange={set("zip_code")} className="bg-zinc-950 border-zinc-700 h-11 mt-1" placeholder="73096" inputMode="numeric" maxLength={10}/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Date of Birth</Label>
          <Input data-testid="rider-profile-dob" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} className="bg-zinc-950 border-zinc-700 h-11 mt-1"/>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Street Address</Label>
          <Input data-testid="rider-profile-address" value={form.address} onChange={set("address")} className="bg-zinc-950 border-zinc-700 h-11 mt-1" placeholder="100 W Tom Stafford Ave"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">City</Label>
          <Input data-testid="rider-profile-city" value={form.city} onChange={set("city")} className="bg-zinc-950 border-zinc-700 h-11 mt-1" placeholder="Weatherford"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">State</Label>
          <Input data-testid="rider-profile-state" value={form.state} onChange={set("state")} className="bg-zinc-950 border-zinc-700 h-11 mt-1" placeholder="OK" maxLength={2}/>
        </div>
        {/* v2.7.7 · Gender-Preferred Dispatch · Self-identification + preferred driver routing */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Your Gender</Label>
          <select
            data-testid="rider-profile-gender"
            value={form.gender}
            onChange={set("gender")}
            className="w-full bg-zinc-950 border border-zinc-700 rounded h-11 mt-1 px-3 text-sm text-zinc-100"
          >
            <option value="unspecified">Unspecified / Prefer not to say</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Preferred Driver Gender</Label>
          <select
            data-testid="rider-profile-preferred-driver-gender"
            value={form.preferred_driver_gender}
            onChange={set("preferred_driver_gender")}
            className="w-full bg-zinc-950 border border-zinc-700 rounded h-11 mt-1 px-3 text-sm text-zinc-100"
          >
            <option value="any">No Preference</option>
            <option value="male">Preferred Male Driver</option>
            <option value="female">Preferred Female Driver</option>
          </select>
        </div>
      </div>

      {/* v2.7.65+E91 · Notification preferences (SMS / Email opt-in) */}
      <div className="mt-5 pt-4 border-t border-zinc-800">
        <div className="mb-3">
          <h3 className="text-sm font-bold text-amber-400 tracking-wide uppercase">Notification Preferences</h3>
          <p className="text-[11px] text-zinc-400 mt-0.5">Choose how we reach you about your bookings. You can also override this per booking.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label
            data-testid="rider-profile-prefers-sms-row"
            className="flex items-start gap-3 p-3 bg-zinc-950 border border-zinc-700 rounded cursor-pointer hover:border-amber-400/40 transition"
          >
            <input
              type="checkbox"
              data-testid="rider-profile-prefers-sms"
              checked={!!form.prefers_sms}
              onChange={(e) => toggle("prefers_sms")(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded accent-amber-400"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-zinc-100">Text me (SMS)</div>
              <div className="text-[11px] text-zinc-400 mt-0.5">Booking confirmations and driver assignment texts to your phone.</div>
            </div>
          </label>
          <label
            data-testid="rider-profile-prefers-email-row"
            className="flex items-start gap-3 p-3 bg-zinc-950 border border-zinc-700 rounded cursor-pointer hover:border-amber-400/40 transition"
          >
            <input
              type="checkbox"
              data-testid="rider-profile-prefers-email"
              checked={!!form.prefers_email}
              onChange={(e) => toggle("prefers_email")(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded accent-amber-400"
            />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-zinc-100">Email me</div>
              <div className="text-[11px] text-zinc-400 mt-0.5">Detailed booking receipts and updates in your inbox.</div>
            </div>
          </label>
        </div>
        {!form.prefers_sms && !form.prefers_email && !form.prefers_push && (
          <div className="mt-2 text-[11px] text-amber-400" data-testid="rider-profile-no-channels-warning">
            Heads up — with all channels off, we&apos;ll only reach you by phone call for urgent updates.
          </div>
        )}

        {/* v2.7.65+E93 · Third channel · Browser Push */}
        <div className="mt-3 p-3 bg-zinc-950 border border-zinc-700 rounded"
             data-testid="rider-profile-push-row">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <label className="flex items-start gap-3 flex-1 cursor-pointer">
              <input
                type="checkbox"
                data-testid="rider-profile-prefers-push"
                checked={!!form.prefers_push}
                onChange={(e) => toggle("prefers_push")(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded accent-amber-400"
              />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100 flex items-center gap-1.5">
                  <Bell className="h-3.5 w-3.5 text-amber-400"/>
                  Browser push notifications
                  {pushSubscribed && (
                    <span
                      data-testid="rider-profile-push-subscribed-badge"
                      className="ml-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold"
                    >
                      Active on this device
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  Instant driver-assigned pings and arrival alerts · no SMS charges.
                  {!pushMeta.pushSupported && (
                    <span className="block mt-1 text-amber-300">
                      {pushMeta.family === "safari" && !pushMeta.isPWA
                        ? "iOS Safari: add to Home Screen first to enable push."
                        : "This browser doesn't support push · SMS + email will still work."}
                    </span>
                  )}
                </div>
              </div>
            </label>
            {pushMeta.pushSupported && (
              pushSubscribed ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={disablePush}
                  disabled={pushBusy}
                  data-testid="rider-profile-push-disable-btn"
                  className="border-zinc-700 hover:border-rose-500/60 text-xs shrink-0"
                >
                  {pushBusy ? <Loader2 className="h-3 w-3 animate-spin"/> : (
                    <><BellOff className="h-3 w-3 mr-1"/>Disable on this device</>
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  onClick={enablePush}
                  disabled={pushBusy}
                  data-testid="rider-profile-push-enable-btn"
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-xs shrink-0"
                >
                  {pushBusy ? <Loader2 className="h-3 w-3 animate-spin"/> : (
                    <><Bell className="h-3 w-3 mr-1"/>Enable now</>
                  )}
                </Button>
              )
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-zinc-800">
        {savedAt ? (
          <div className="flex items-center gap-1.5 text-[11px] text-green-400" data-testid="rider-profile-saved">
            <CheckCircle2 className="h-3.5 w-3.5"/>Saved {new Date(savedAt).toLocaleTimeString()}
          </div>
        ) : (
          <div className="text-[11px] text-zinc-500">Your info stays on your account · used to pre-fill ride bookings.</div>
        )}
        <Button data-testid="rider-profile-save" onClick={save} disabled={busy} className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Save className="h-4 w-4 mr-1"/>SAVE PROFILE</>}
        </Button>
      </div>
      {showIosA2hs && <IosA2hsModal onClose={() => setShowIosA2hs(false)}/>}
    </Card>
    <SavedPlacesManager customer={customer} onChanged={onSaved}/>
    </div>
  );
}


// ═══ v2.7.65+E255 · Saved Places manager — add/remove Home/Work/Favorites ═══
function SavedPlacesManager({ customer, onChanged }) {
  const places = customer?.saved_places || [];
  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!label.trim() || !address.trim()) { toast.error("Enter a label and an address"); return; }
    setBusy(true);
    try {
      await customerApi.post("/customer/saved-places", { label: label.trim(), address: address.trim() });
      await onChanged?.();
      toast.success(`Saved "${label.trim()}"`);
      setLabel(""); setAddress("");
    } catch {
      toast.error("Could not save place");
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try {
      await customerApi.delete(`/customer/saved-places/${id}`);
      await onChanged?.();
      toast.message("Place removed");
    } catch {
      toast.error("Could not remove place");
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="rider-saved-places-manager">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="h-5 w-5 text-amber-400"/>
        <h3 className="text-base font-bold">Saved Places</h3>
      </div>
      <p className="text-xs text-zinc-400 mb-4">Save your regular spots (Work, Gym, Mom's) for one-tap drop-off when you book a ride.</p>
      <div className="space-y-2 mb-4">
        {places.length === 0 && <div className="text-xs text-zinc-500 italic" data-testid="saved-places-empty">No saved places yet — add one below, or tap "Save this place" while booking.</div>}
        {places.map((p) => (
          <div key={p.id} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2" data-testid={`saved-place-row-${p.id}`}>
            <Star className="h-4 w-4 text-amber-400 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{p.label}</div>
              <div className="text-[11px] text-zinc-500 truncate">{p.address}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300 h-8 w-8 shrink-0" data-testid={`saved-place-remove-${p.id}`}><Trash2 className="h-4 w-4"/></Button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr_auto] gap-2 items-end">
        <div><Label className="text-[10px] uppercase tracking-wider text-zinc-400">Label</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Work" className="bg-zinc-950 border-zinc-700 h-10 mt-1" data-testid="saved-place-new-label"/></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-zinc-400">Address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St, Weatherford, OK" className="bg-zinc-950 border-zinc-700 h-10 mt-1" data-testid="saved-place-new-address"/></div>
        <Button onClick={add} disabled={busy} className="h-10 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold" data-testid="saved-place-add-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <><Plus className="h-4 w-4 mr-1"/>Add</>}</Button>
      </div>
    </Card>
  );
}


// ═══ v2.7.65+E97 · iOS Safari Add-to-Home-Screen guide ═══════════════
// Rendered ONLY when a rider on iOS Safari (not yet in PWA mode) taps
// "Enable now" on the push toggle. Walks them through the exact taps
// needed to install the app so they can enable Web Push properly.
function IosA2hsModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="ios-a2hs-modal"
    >
      <div
        className="bg-zinc-900 border border-amber-500/40 rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center">
              <Smartphone className="h-5 w-5 text-amber-400"/>
            </div>
            <div>
              <h3 className="font-bold text-lg text-zinc-100">Enable push on iPhone</h3>
              <p className="text-[11px] text-zinc-400">3 quick taps to install · then push works.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="ios-a2hs-close"
            className="text-zinc-500 hover:text-zinc-100 p-1"
            aria-label="Close"
          >
            <X className="h-4 w-4"/>
          </button>
        </div>

        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 mb-4 text-[12px] text-amber-200">
          Apple only allows browser notifications for apps you add to your Home Screen.
          Once installed, open Buller Taxi from the Home Screen icon and toggle push back on.
        </div>

        <ol className="space-y-3">
          <A2hsStep
            step={1}
            icon={<Share className="h-4 w-4 text-sky-400"/>}
            title="Tap the Share button"
            body={<span>It&apos;s the square icon with the ↑ arrow at the bottom of Safari (or the top on iPad).</span>}
          />
          <A2hsStep
            step={2}
            icon={<Plus className="h-4 w-4 text-emerald-400"/>}
            title={<span>Choose &ldquo;Add to Home Screen&rdquo;</span>}
            body="Scroll the share sheet if you don't see it — it's near the bottom of the list."
          />
          <A2hsStep
            step={3}
            icon={<Smartphone className="h-4 w-4 text-amber-400"/>}
            title="Open from the Home Screen"
            body={<span>Tap the new Buller Taxi icon, come back to <strong className="text-amber-300">Profile → Notification Preferences</strong>, and press <strong className="text-amber-300">Enable now</strong> again.</span>}
          />
        </ol>

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="text-[10px] text-zinc-500">
            SMS + email still work · you don&apos;t have to install to book a ride.
          </div>
          <Button
            onClick={onClose}
            data-testid="ios-a2hs-ok"
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-xs h-8"
          >
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
}

function A2hsStep({ step, icon, title, body }) {
  return (
    <li className="flex items-start gap-3">
      <div className="h-7 w-7 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-xs font-black text-zinc-300 flex-shrink-0">
        {step}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-bold text-zinc-100">
          {icon}
          {title}
        </div>
        <div className="text-[11px] text-zinc-400 mt-0.5 leading-relaxed">{body}</div>
      </div>
    </li>
  );
}
